import { OrganizationList } from '@/components'
import { useOpenOrganisation, usePositionStorage } from '@/hooks'
import { useAuth } from '@/lib'
import {
	AuthErrorAlert,
	AuthInput,
	AuthLayout,
	AuthSubmitButton,
	EmailVerificationForm,
	JobillaBrandLogo,
	logger,
} from '@/ui'
import type {
	AuthAcceptInvitation403,
	AuthAcceptInvitationError,
	OrganisationAcceptInvitationByTokenResponse,
	OrganisationPeekInvitationResponse,
} from '@jobilla/nexus-sdk'
import {
	authAcceptInvitation,
	authSelectOrganisation,
	authSetPasswordByInvitation,
	authSsoGoogle,
	organisationPeekInvitation,
	useOrganisationAcceptInvitationByToken,
} from '@jobilla/nexus-sdk'
import { createFileRoute } from '@tanstack/react-router'
import { isAxiosError } from 'axios'
import confetti from 'canvas-confetti'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

export const Route = createFileRoute('/invite')({
	validateSearch: z.object({
		invitation_token: z.string(),
	}),
	component: InviteComponent,
})

type PeekData = OrganisationPeekInvitationResponse

// Every auth endpoint on this page surfaces the same WorkOS challenge body on a
// 403. Its keys are built by hand on the backend rather than by a DTO, so they
// stay snake_case whatever the mapper does.
type AuthChallenge = AuthAcceptInvitation403

function authChallenge(err: unknown): AuthChallenge | null {
	return isAxiosError<AuthChallenge>(err) && err.response?.status === 403 ? err.response.data : null
}

function responseMessage(err: unknown): string | undefined {
	return isAxiosError<{ message?: string }>(err) ? err.response?.data?.message : undefined
}

interface EmailVerificationState {
	email: string
	pendingAuthenticationToken: string
}

interface OrgSelectionState {
	pendingAuthenticationToken: string
	organizations: Array<{ id: string; name: string }>
}

function InviteComponent() {
	const { t } = useTranslation('common')
	const { invitation_token } = Route.useSearch()
	const openOrganisation = useOpenOrganisation()
	const { isSignedIn, isLoading: isAuthLoading } = useAuth()
	const { saveInviteToken } = usePositionStorage()

	const [error, setError] = useState<string | null>(null)
	const [peek, setPeek] = useState<PeekData | null>(null)
	const [organisation, setOrganisation] = useState<OrganisationAcceptInvitationByTokenResponse | null>(null)
	const [isSwitching, setIsSwitching] = useState(false)
	const [password, setPassword] = useState('')
	const [confirmPassword, setConfirmPassword] = useState('')
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [verification, setVerification] = useState<EmailVerificationState | null>(null)
	const [orgSelection, setOrgSelection] = useState<OrgSelectionState | null>(null)
	const [ssoHint, setSsoHint] = useState(false)
	const peekCalledRef = useRef(false)

	const buildTooManyAttemptsMessage = useCallback((err: unknown): string | null => {
		if (!isAxiosError(err) || err.response?.status !== 429) { return null }
		const retryAfter = Number(err.response.headers['retry-after'])
		if (Number.isFinite(retryAfter) && retryAfter > 0) {
			const fallback = retryAfter === 1
				? 'Too many attempts. Please wait {{count}} second before trying again.'
				: 'Too many attempts. Please wait {{count}} seconds before trying again.'
			return t('auth.invite.errorTooManyAttemptsWait', fallback, { count: retryAfter })
		}
		return t(
			'auth.invite.errorTooManyAttempts',
			'Too many attempts. Please wait a minute before trying again.',
		)
	}, [t])

	const handleTooManyAttempts = useCallback((err: unknown, action: string): boolean => {
		const tooManyMsg = buildTooManyAttemptsMessage(err)
		if (tooManyMsg === null) { return false }
		logger.warn(`Rate limited on ${action}`, {
			category: 'auth',
			data: { action, status: 429 },
		})
		setError(tooManyMsg)
		setIsSubmitting(false)
		return true
	}, [buildTooManyAttemptsMessage])

	const acceptMutation = useOrganisationAcceptInvitationByToken({
		onSuccess: (data) => {
			setOrganisation(data)
		},
		onError: (err) => {
			if (handleTooManyAttempts(err, 'invite-accept')) { return }
			const status = err?.response?.status
			if (status && status >= 400 && status < 500) {
				logger.warn('Recoverable error on invite-accept', {
					category: 'auth',
					data: {
						action: 'invite-accept',
						status,
						message: responseMessage(err),
					},
				})
			} else {
				logger.error(err, { category: 'auth', data: { action: 'invite-accept' } })
			}
			const message = responseMessage(err) || t('auth.invite.errorDefault', 'Failed to accept invitation.')
			setError(message)
		},
	})

	const accepted = organisation !== null

	// Confetti on acceptance
	useEffect(() => {
		if (!accepted) { return }

		const colors = ['#b365f6', '#9a4bf5', '#872cf0', '#710ce2', '#cb87fb', '#f0cefd']
		const duration = 2500
		const end = Date.now() + duration

		const frame = () => {
			void confetti({ particleCount: 4, colors, angle: 60, spread: 55, origin: { x: 0, y: 0.6 } })
			void confetti({ particleCount: 4, colors, angle: 120, spread: 55, origin: { x: 1, y: 0.6 } })
			if (Date.now() < end) { requestAnimationFrame(frame) }
		}

		frame()
	}, [accepted])

	// Peek on mount
	useEffect(() => {
		if (peekCalledRef.current) { return }
		peekCalledRef.current = true

		void organisationPeekInvitation(invitation_token).then((res) => {
			setPeek(res.data)
		}).catch((err) => {
			if (isAxiosError(err) && err.response?.status && err.response.status >= 400 && err.response.status < 500) {
				logger.warn('Recoverable error on invite-peek', {
					category: 'auth',
					data: {
						action: 'invite-peek',
						status: err.response.status,
						message: responseMessage(err),
					},
				})
			} else {
				logger.error(err, { category: 'auth', data: { action: 'invite-peek' } })
			}
			const message = responseMessage(err) ?? t('auth.invite.errorDefault', 'Failed to accept invitation.')
			setError(message)
		})
	}, [invitation_token, t])

	// Existing user: sign in + accept via /auth/accept-invitation
	const handleSignInAndAccept = useCallback(async () => {
		if (!peek) { return }
		setError(null)
		setSsoHint(false)
		setIsSubmitting(true)

		try {
			const { data } = await authAcceptInvitation({
				invitationToken: invitation_token,
				email: peek.email,
				password,
			}, { withCredentials: true })

			const slug = data.organisationSlug
			window.location.href = slug ? `/company/${slug}` : '/company'
		} catch (err) {
			const challenge = authChallenge(err)
			if (challenge?.code === 'email_verification_required' && challenge.pending_authentication_token) {
				setVerification({
					email: challenge.email ?? peek.email,
					pendingAuthenticationToken: challenge.pending_authentication_token,
				})
				setIsSubmitting(false)
				return
			}
			if (
				challenge?.code === 'organization_selection_required'
				&& challenge.pending_authentication_token
				&& challenge.organizations
			) {
				setOrgSelection({
					pendingAuthenticationToken: challenge.pending_authentication_token,
					organizations: challenge.organizations,
				})
				setIsSubmitting(false)
				return
			}
			if (handleTooManyAttempts(err, 'invite-accept-auth')) { return }
			if (isAxiosError(err) && (err.response?.status === 400 || err.response?.status === 422)) {
				const data: AuthAcceptInvitationError = err.response.data
				logger.warn(`Recoverable ${err.response.status} on accept-invitation`, {
					category: 'auth',
					data: {
						action: 'invite-accept-auth',
						status: `recoverable_${err.response.status}`,
						code: data.code,
						message: data.message,
					},
				})
				if (data.code === 'sso_required') {
					setSsoHint(true)
				} else {
					setError(data.message ?? t('auth.invite.errorDefault', 'Failed to accept invitation.'))
				}
				setIsSubmitting(false)
				return
			}
			logger.error(err, { category: 'auth', data: { action: 'invite-accept-auth' } })
			const message = responseMessage(err) ?? t('auth.invite.errorDefault', 'Failed to accept invitation.')
			setError(message)
			setIsSubmitting(false)
		}
	}, [peek, invitation_token, password, t, handleTooManyAttempts])

	// New user: set password + accept via /auth/set-password-by-invitation
	const handleCreateAccountAndAccept = useCallback(async () => {
		if (!peek) { return }
		setError(null)

		if (password !== confirmPassword) {
			setError(t('auth.inviteSetup.passwordsMismatch', 'Passwords do not match'))
			return
		}

		setIsSubmitting(true)

		try {
			const { data } = await authSetPasswordByInvitation({
				invitationToken: invitation_token,
				password,
			}, { withCredentials: true })

			window.location.href = data.organisationSlug ? `/company/${data.organisationSlug}` : '/company'
		} catch (err) {
			const challenge = authChallenge(err)
			if (challenge?.code === 'email_verification_required' && challenge.pending_authentication_token) {
				setVerification({
					email: challenge.email ?? peek.email,
					pendingAuthenticationToken: challenge.pending_authentication_token,
				})
				setIsSubmitting(false)
				return
			}
			if (
				challenge?.code === 'organization_selection_required'
				&& challenge.pending_authentication_token
				&& challenge.organizations
			) {
				setOrgSelection({
					pendingAuthenticationToken: challenge.pending_authentication_token,
					organizations: challenge.organizations,
				})
				setIsSubmitting(false)
				return
			}
			if (isAxiosError<{ code?: string }>(err) && err.response?.status === 503) {
				if (err.response.data.code === 'authentication_unavailable') {
					logger.warn('WorkOS authenticate unavailable on set-password-by-invitation', {
						category: 'auth',
						data: { action: 'invite-create-account', status: 'authentication_unavailable' },
					})
					setError(t(
						'auth.inviteSetup.errorAuthenticationUnavailable',
						'Your password was saved. We could not finish signing you in — please try again.',
					))
					setPeek((current) => current ? { ...current, hasAuthenticated: true } : current)
					setIsSubmitting(false)
					return
				}
			}
			if (handleTooManyAttempts(err, 'invite-create-account')) { return }
			if (isAxiosError(err) && (err.response?.status === 400 || err.response?.status === 422)) {
				const data: AuthAcceptInvitationError = err.response.data
				logger.warn(`Recoverable ${err.response.status} on set-password-by-invitation`, {
					category: 'auth',
					data: {
						action: 'invite-create-account',
						status: `recoverable_${err.response.status}`,
						code: data.code,
						message: data.message,
					},
				})
				setError(data.message ?? t('auth.invite.errorDefault', 'Failed to accept invitation.'))
				setIsSubmitting(false)
				return
			}
			logger.error(err, { category: 'auth', data: { action: 'invite-create-account' } })
			const message = responseMessage(err) ?? t('auth.invite.errorDefault', 'Failed to accept invitation.')
			setError(message)
			setIsSubmitting(false)
		}
	}, [peek, invitation_token, password, confirmPassword, t, handleTooManyAttempts])

	// Google SSO: save invite token for callback, then redirect to Google
	const handleGoogleSignIn = useCallback(async () => {
		if (!peek) { return }

		// Save invitation token so the callback can pass it to WorkOS (sessionStorage, tab-scoped)
		saveInviteToken(invitation_token)

		try {
			const { data } = await authSsoGoogle({ invitation_token })
			window.location.href = data.authorizationUrl
		} catch (err) {
			if (isAxiosError(err) && err.response?.status && err.response.status >= 400 && err.response.status < 500) {
				logger.warn('Recoverable error on invite-sso-google', {
					category: 'auth',
					data: {
						action: 'invite-sso-google',
						status: err.response.status,
						message: responseMessage(err),
					},
				})
			} else {
				logger.error(err, { category: 'auth', data: { action: 'invite-sso-google' } })
			}
			setError(t('auth.invite.errorDefault', 'Failed to accept invitation.'))
		}
	}, [peek, invitation_token, saveInviteToken, t])

	const handleGoToCompany = useCallback(async () => {
		if (!organisation) { return }
		setIsSwitching(true)
		await openOrganisation(organisation)
	}, [organisation, openOrganisation])

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault()
		if (peek?.hasAuthenticated) {
			void handleSignInAndAccept()
		} else {
			void handleCreateAccountAndAccept()
		}
	}

	// --- Render states ---

	if (orgSelection) {
		return (
			<AuthLayout>
				<OrganizationList
					organizations={orgSelection.organizations}
					onSelect={(org) => {
						void (async () => {
							try {
								const { data } = await authSelectOrganisation({
									pendingAuthenticationToken: orgSelection.pendingAuthenticationToken,
									organisationId: org.id,
								}, { withCredentials: true })
								const slug = data.organisationSlug
								window.location.href = slug ? `/company/${slug}` : '/company'
							} catch (err) {
								if (
									isAxiosError(err) && err.response?.status && err.response.status >= 400
									&& err.response.status < 500
								) {
									logger.warn('Recoverable error on invite-org-selection', {
										category: 'auth',
										data: {
											action: 'invite-org-selection',
											status: err.response.status,
											message: responseMessage(err),
										},
									})
								} else {
									logger.error(err, { category: 'auth', data: { action: 'invite-org-selection' } })
								}
								setOrgSelection(null)
								setError(t('auth.invite.errorDefault', 'Failed to accept invitation.'))
							}
						})()
					}}
				/>
			</AuthLayout>
		)
	}

	if (verification) {
		return (
			<AuthLayout>
				<EmailVerificationForm
					email={verification.email}
					pendingAuthenticationToken={verification.pendingAuthenticationToken}
					onOrganizationSelectionRequired={(data) => {
						setVerification(null)
						setOrgSelection(data)
					}}
				/>
			</AuthLayout>
		)
	}

	if (error && !peek) {
		return (
			<AuthLayout>
				<div className="w-full max-w-lg mx-auto p-8">
					<JobillaBrandLogo />
					<h1 className="mt-8 text-3xl font-semibold text-gray-800 tracking-[0.01em]">
						{t('auth.invite.errorTitle', 'Invalid invitation')}
					</h1>
					<p className="mt-3 text-base text-gray-500">{error}</p>
					<a
						href="/auth"
						className="mt-8 w-full py-3 px-4 rounded-full font-medium text-white text-base bg-gradient-to-r from-[#225DFC] to-[#9726FA] hover:brightness-110 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-[#6366F1]/50 focus:ring-offset-2 shadow-lg shadow-[#225DFC]/30 transition-all duration-300 flex items-center justify-center"
					>
						{t('auth.callback.backToSignIn', 'Back to sign in')}
					</a>
				</div>
			</AuthLayout>
		)
	}

	if (accepted) {
		return (
			<AuthLayout>
				<div className="w-full max-w-lg mx-auto p-8">
					<JobillaBrandLogo />
					<div className="mt-8 flex flex-col items-center gap-6 text-center">
						<h1 className="text-4xl font-thin tracking-wide text-[#6366F1]">
							{t('auth.invite.welcomeTitle', "You're in!")}
						</h1>
						<p className="text-lg text-gray-600 max-w-md">
							{t(
								'auth.invite.welcomeDescription',
								'You have joined {{companyName}}. Welcome to the team!',
								{
									companyName: organisation.name,
								},
							)}
						</p>
						<button
							type="button"
							onClick={handleGoToCompany}
							disabled={isSwitching}
							className="mt-4 py-3 px-8 rounded-full font-medium text-white text-base bg-gradient-to-r from-[#225DFC] to-[#9726FA] hover:brightness-110 hover:shadow-xl hover:translate-y-[-1px] focus:outline-none focus:ring-2 focus:ring-[#6366F1]/50 focus:ring-offset-2 shadow-lg shadow-[#225DFC]/30 transition-all duration-300 inline-flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
						>
							{isSwitching
								? t('auth.invite.switching', 'Switching...')
								: t('auth.invite.goToCompany', 'Go to {{companyName}}', {
									companyName: organisation.name,
								})}
						</button>
					</div>
				</div>
			</AuthLayout>
		)
	}

	if (!peek || isAuthLoading) {
		return (
			<AuthLayout>
				<div className="w-full max-w-lg mx-auto p-8 text-center">
					<JobillaBrandLogo />
					<p className="mt-8 text-gray-400 animate-pulse">
						{t('auth.invite.loading', 'Loading...')}
					</p>
				</div>
			</AuthLayout>
		)
	}

	// Signed in — show accept button
	if (isSignedIn) {
		return (
			<AuthLayout>
				<div className="w-full max-w-lg mx-auto p-8">
					<JobillaBrandLogo />
					<InviteCard orgName={peek.organisationName} email={peek.email} />
					{error
						? (
							<div className="mt-4">
								<AuthErrorAlert message={error} />
							</div>
						)
						: null}
					<button
						type="button"
						onClick={() => acceptMutation.mutate({ token: invitation_token })}
						disabled={acceptMutation.isPending}
						className="mt-8 w-full py-3 px-4 rounded-full font-medium text-white text-base bg-gradient-to-r from-[#225DFC] to-[#9726FA] hover:brightness-110 hover:shadow-xl hover:translate-y-[-1px] focus:outline-none focus:ring-2 focus:ring-[#6366F1]/50 focus:ring-offset-2 shadow-lg shadow-[#225DFC]/30 transition-all duration-300 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
					>
						{acceptMutation.isPending
							? t('auth.invite.accepting', 'Accepting invitation...')
							: t('auth.invite.accept', 'Accept invitation')}
					</button>
				</div>
			</AuthLayout>
		)
	}

	// Not signed in — show auth form
	const isExistingUser = peek.hasAuthenticated

	return (
		<AuthLayout>
			<div className="w-full max-w-lg mx-auto p-8">
				<JobillaBrandLogo />
				<InviteCard orgName={peek.organisationName} email={peek.email} />

				<form onSubmit={handleSubmit} className="mt-8 space-y-6">
					<AuthErrorAlert message={error} />

					{ssoHint
						? (
							<div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
								{t(
									'auth.invite.ssoHint',
									'This account uses Google sign-in. Please use the button below.',
								)}
							</div>
						)
						: null}

					<AuthInput
						label={t('auth.inviteSetup.passwordLabel', 'Password')}
						type="password"
						placeholder=""
						value={password}
						onChange={setPassword}
						required={true}
						minLength={10}
						autoComplete={isExistingUser ? 'current-password' : 'new-password'}
					/>

					{!isExistingUser && (
						<AuthInput
							label={t('auth.inviteSetup.confirmPasswordLabel', 'Confirm password')}
							type="password"
							placeholder=""
							value={confirmPassword}
							onChange={setConfirmPassword}
							required={true}
							minLength={10}
							autoComplete="new-password"
						/>
					)}

					<AuthSubmitButton isLoading={isSubmitting} disabled={isSubmitting}>
						{isExistingUser
							? t('auth.invite.signInAndAccept', 'Sign in & Accept')
							: t('auth.invite.createAndAccept', 'Create account & join')}
					</AuthSubmitButton>
				</form>

				{isExistingUser
					? (
						<>
							<div className="mt-6 flex items-center gap-3">
								<div className="flex-1 h-px bg-gray-200" />
								<span className="text-sm text-gray-400">{t('auth.signIn.or', 'or')}</span>
								<div className="flex-1 h-px bg-gray-200" />
							</div>

							<button
								type="button"
								onClick={() => void handleGoogleSignIn()}
								className="mt-6 w-full flex items-center justify-center gap-3 px-4 py-3 rounded-full border border-gray-200 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
							>
								<svg viewBox="0 0 24 24" width="20" height="20">
									<path
										d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
										fill="#4285F4"
									/>
									<path
										d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
										fill="#34A853"
									/>
									<path
										d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
										fill="#FBBC05"
									/>
									<path
										d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
										fill="#EA4335"
									/>
								</svg>
								{t('auth.signIn.googleSignIn', 'Sign in with Google')}
							</button>
						</>
					)
					: null}
			</div>
		</AuthLayout>
	)
}

function InviteCard({ orgName, email }: { orgName: string; email: string }) {
	const { t } = useTranslation('common')

	return (
		<>
			<div className="mt-8">
				<h1 className="text-3xl font-semibold text-gray-800 tracking-[0.01em]">
					{t('auth.invite.title', "You've been invited")}
				</h1>
				<p className="mt-3 text-base text-gray-500">
					{t('auth.invite.description', 'You have been invited to join {{companyName}}.', {
						companyName: orgName,
					})}
				</p>
			</div>

			<div className="mt-6 flex items-center gap-4 p-4 rounded-xl border border-gray-200 bg-gray-50">
				<div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#225DFC] to-[#9726FA] flex items-center justify-center text-white font-semibold text-sm shrink-0">
					{orgName.charAt(0).toUpperCase()}
				</div>
				<div className="min-w-0">
					<div className="font-medium text-gray-800 truncate">{orgName}</div>
					<div className="text-sm text-gray-400 truncate">{email}</div>
				</div>
			</div>
		</>
	)
}
